"use client";

import { useEffect, useId, useState } from "react";
import {
  Check,
  ChevronRight,
  Copy,
  LockKeyhole,
  Plus,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  Field,
} from "../../design-system";
import { ConsoleFab } from "./console-fab";
import {
  createRbacFixture,
  rbacMemberFixture,
  rolesForScope,
  type RbacExample,
  type RbacMember,
  type RbacRole,
  type RbacRoleDraft,
  type RbacScope,
  type RbacView,
} from "./rbac-data";
import "./rbac.css";
export type {
  RbacExample,
  RbacMember,
  RbacPermission,
  RbacRole,
  RbacRoleDraft,
  RbacScope,
  RbacView,
} from "./rbac-data";

export function RoleChips({
  roles,
}: {
  roles: { id: string; name: string }[];
}) {
  return (
    <span className="mcr-role-chips">
      {roles.length ? (
        roles.map((role) => <Badge key={role.id}>{role.name}</Badge>)
      ) : (
        <span className="mcr-muted">역할 미지정</span>
      )}
    </span>
  );
}
export function RoleMultiSelect({
  availableRoles,
  selectedIds,
  onChange,
  scope,
  customerId = null,
  disabled = false,
  lastAdminLockedId,
}: {
  availableRoles: RbacRole[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  scope: RbacScope;
  customerId?: string | null;
  disabled?: boolean;
  lastAdminLockedId?: string;
}) {
  const [search, setSearch] = useState("");
  const visible = rolesForScope(availableRoles, scope, customerId).filter(
    (role) =>
      `${role.name} ${role.description}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  return (
    <div className="mcr-role-select">
      <Field
        type="search"
        label="역할 검색"
        placeholder="이름 또는 설명으로 찾기"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {lastAdminLockedId && (
        <p className="mcr-notice" role="note">
          <LockKeyhole size={16} />
          <span>
            마지막 시스템 관리자입니다. 시스템 관리자 역할을 뺄 수 없습니다.
          </span>
        </p>
      )}
      <div className="mcr-role-options">
        {visible.map((role) => {
          const selected = selectedIds.includes(role.id),
            protectedRole = role.id === lastAdminLockedId && selected;
          const unavailable = role.assignable === false && !selected;
          return (
            <label
              key={role.id}
              className={`mcr-role-option ${selected ? "is-selected" : ""} ${protectedRole || unavailable ? "is-restricted" : ""}`}
            >
              <input
                type="checkbox"
                aria-label={role.name}
                checked={selected}
                disabled={disabled || protectedRole || unavailable}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selectedIds, role.id]
                      : selectedIds.filter((id) => id !== role.id),
                  )
                }
              />
              <span>
                <strong>
                  {role.name}
                  {scope === "customer" && (
                    <Badge>{role.shared ? "공용" : "이 고객사 전용"}</Badge>
                  )}
                  {role.locked && (
                    <LockKeyhole size={13} aria-label="잠긴 역할" />
                  )}
                </strong>
                <small>{role.description}</small>
                {unavailable && (
                  <small className="mcr-reason">
                    {role.assignmentDisabledReason ||
                      "내게 없는 권한을 포함한 역할입니다."}
                  </small>
                )}
                {protectedRole && (
                  <small className="mcr-reason">
                    마지막 시스템 관리자 보호
                  </small>
                )}
              </span>
              <Badge>{role.permissions.length}개 권한</Badge>
            </label>
          );
        })}
        {visible.length === 0 && (
          <p className="mcr-empty-inline">선택할 수 있는 역할이 없습니다.</p>
        )}
      </div>
      <p className="mcr-muted">
        {selectedIds.length}개 선택 · 선택한 역할의 권한을 함께 사용합니다.
      </p>
    </div>
  );
}
export function RoleAssignmentDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  selectedIds: string[];
  availableRoles: RbacRole[];
  scope: RbacScope;
  customerId?: string | null;
  canManage?: boolean;
  lastAdminLockedId?: string;
  readOnlyReason?: string;
  onSave?: (ids: string[]) => Promise<void>;
}) {
  if (!props.open) return null;
  return <RoleAssignmentContent {...props} />;
}
function RoleAssignmentContent({
  open,
  onOpenChange,
  memberName,
  selectedIds,
  availableRoles,
  scope,
  customerId = null,
  canManage = true,
  lastAdminLockedId,
  readOnlyReason,
  onSave,
}: Parameters<typeof RoleAssignmentDialog>[0]) {
  const [selected, setSelected] = useState([...selectedIds]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const editable = canManage && !!onSave;
  const changed =
    selected.length !== selectedIds.length ||
    selected.some((id) => !selectedIds.includes(id));
  const save = async () => {
    if (!editable || busy || !onSave) return;
    if (!selected.length) {
      setError("역할을 하나 이상 고르세요.");
      return;
    }
    if (
      lastAdminLockedId &&
      selectedIds.includes(lastAdminLockedId) &&
      !selected.includes(lastAdminLockedId)
    ) {
      setError("마지막 시스템 관리자 역할은 뺄 수 없습니다.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave(selected);
      onOpenChange(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "역할을 지정하지 못했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title={`${memberName} · 역할 지정`}
      description="여러 역할을 선택하면 각 역할의 권한을 함께 사용합니다."
      size="wide"
      footer={
        <DialogActions
          secondary={{
            label: editable ? "취소" : "닫기",
            onClick: () => onOpenChange(false),
            disabled: busy,
          }}
          primary={
            editable
              ? {
                  label: "역할 저장",
                  onClick: save,
                  disabled: !changed || busy,
                  "aria-busy": busy,
                }
              : undefined
          }
        />
      }
    >
      <div className="mcr-form">
        <p className="mcr-scope-note">
          {scope === "staff"
            ? "관리 콘솔 역할"
            : "고객사 공용 역할과 이 고객사 전용 역할"}
        </p>
        {!editable && (
          <p className="mcr-notice">
            <LockKeyhole size={16} />
            <span>
              {readOnlyReason || "역할 지정 권한이 없어 읽기만 할 수 있습니다."}
            </span>
          </p>
        )}
        <RoleMultiSelect
          availableRoles={availableRoles}
          selectedIds={selected}
          onChange={setSelected}
          scope={scope}
          customerId={customerId}
          disabled={!editable || busy}
          lastAdminLockedId={lastAdminLockedId}
        />
        {error && (
          <p className="mcr-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

type ManagerProps = {
  scope?: RbacScope;
  customerId?: string | null;
  customerName?: string;
  embedded?: boolean;
  example?: RbacExample;
  view?: RbacView;
  onSave?: (draft: RbacRoleDraft) => Promise<void | RbacView>;
  onDelete?: (roleId: string) => Promise<void | RbacView>;
  onLoadMembers?: (roleId: string) => Promise<RbacMember[]>;
  onReload?: () => Promise<RbacView>;
  onDirtyChange?: (dirty: boolean) => void;
};
function initialFixture(
  scope: RbacScope,
  customerId: string | null,
  customerName: string,
  example: RbacExample,
) {
  const fixture = createRbacFixture(scope, customerId, customerName);
  if (example === "empty")
    fixture.roles = fixture.roles.filter((role) => role.builtin);
  if (example === "readonly" || example === "suspended") {
    fixture.canManage = false;
    fixture.readOnlyReason =
      example === "suspended"
        ? "정지된 고객사는 읽기만 할 수 있습니다. 이용이 재개되면 역할을 변경할 수 있습니다."
        : "역할 관리 권한이 없어 읽기만 할 수 있습니다.";
  }
  if (example === "restricted")
    fixture.permissions = fixture.permissions.map((p) =>
      [
        "USER_MANAGE",
        "BILLING_MANAGE",
        "ROLE_MANAGE",
        "LLM_KEY_MANAGE",
        "MEMBER_MANAGE",
      ].includes(p.code)
        ? { ...p, grantable: false, disabledReason: "내게 없는 권한" }
        : p,
    );
  return fixture;
}
export function RbacManager({
  scope = "staff",
  customerId = "onyou",
  customerName = "온유의원",
  embedded = false,
  example = "normal",
  view,
  onSave,
  onDelete,
  onLoadMembers,
  onReload,
  onDirtyChange,
}: ManagerProps) {
  const formId = useId();
  const [fixture, setFixture] = useState(() =>
    initialFixture(scope, customerId, customerName, example),
  );
  const data = view ?? fixture;
  const sharedScope = data.scope === "customer" && data.customerId === null;
  const createLabel = sharedScope
    ? "공용 역할 만들기"
    : data.scope === "staff"
      ? "관리 콘솔 역할 만들기"
      : "역할 만들기";
  const scopedRoles = rolesForScope(data.roles, data.scope, data.customerId);
  const permissions = data.permissions.filter((p) =>
    p.scopes.includes(data.scope),
  );
  const editable = data.canManage && (!view || !!onSave);
  const initialRole =
    example === "locked"
      ? scopedRoles.find((role) => role.locked)
      : ["restricted", "error"].includes(example)
        ? scopedRoles.find((role) => !role.builtin)
        : undefined;
  const blankDraft = (): RbacRoleDraft => ({
    scope: data.scope,
    customerId: data.customerId,
    shared: sharedScope,
    name: "",
    description: "",
    permissions: [],
  });
  const [draft, setDraft] = useState<RbacRoleDraft | null>(() =>
    initialRole ? { ...initialRole } : example === "new" ? blankDraft() : null,
  );
  const [baseline, setBaseline] = useState<RbacRoleDraft | null>(() => draft);
  const [search, setSearch] = useState(""),
    [permissionSearch, setPermissionSearch] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(
      example === "error"
        ? "동일한 이름의 역할이 있습니다. 다른 이름을 입력해 주세요. (409)"
        : "",
    ),
    [notice, setNotice] = useState("");
  const [discard, setDiscard] = useState(false),
    [copyNotice, setCopyNotice] = useState("");
  const [deleting, setDeleting] = useState<RbacRole | null>(() =>
    example === "in-use"
      ? (scopedRoles.find((role) => !role.builtin) ?? null)
      : null,
  );
  const [members, setMembers] = useState<RbacMember[]>(() =>
    !view && example === "in-use" ? rbacMemberFixture : [],
  );
  const [membersBusy, setMembersBusy] = useState(
      !!view && example === "in-use",
    ),
    [membersLoaded, setMembersLoaded] = useState(!view && example === "in-use"),
    [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [initialDeleteRoleId] = useState(() => deleting?.id);
  const hasExternalView = !!view;
  useEffect(() => {
    if (!initialDeleteRoleId || !hasExternalView) return;
    let active = true;
    const request = onLoadMembers
      ? onLoadMembers(initialDeleteRoleId)
      : Promise.reject(
          new Error("역할이 지정된 사용자 목록을 먼저 불러와야 합니다."),
        );
    request
      .then((result) => {
        if (active) {
          setMembers(result);
          setMembersLoaded(true);
        }
      })
      .catch((cause: unknown) => {
        if (active)
          setError(
            cause instanceof Error
              ? cause.message
              : "사용자 목록을 불러오지 못했습니다.",
          );
      })
      .finally(() => {
        if (active) setMembersBusy(false);
      });
    return () => {
      active = false;
    };
  }, [initialDeleteRoleId, hasExternalView, onLoadMembers]);
  const [assignmentOpen, setAssignmentOpen] = useState(
    example === "last-admin",
  );
  const original = draft?.id
    ? scopedRoles.find((role) => role.id === draft.id)
    : undefined;
  const locked = !!original?.locked;
  const sharedReadOnly = !!original?.shared && !sharedScope;
  const canEditDraft = editable && !locked && !sharedReadOnly;
  const dirty =
    !!draft &&
    !!baseline &&
    (draft.name !== baseline.name ||
      draft.description !== baseline.description ||
      draft.permissions.length !== baseline.permissions.length ||
      draft.permissions.some((code) => !baseline.permissions.includes(code)));
  const added =
    draft?.permissions.filter((code) => !baseline?.permissions.includes(code))
      .length ?? 0;
  const removed =
    baseline?.permissions.filter((code) => !draft?.permissions.includes(code))
      .length ?? 0;
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  const begin = (role?: RbacRole, copy = false) => {
    const allowed = permissions
      .filter((p) => p.grantable && !p.locked)
      .map((p) => p.code);
    const next = role
      ? {
          scope: data.scope,
          customerId: data.customerId,
          shared: sharedScope,
          ...(copy ? { sourceRoleId: role.id } : { id: role.id }),
          name: copy ? `${role.name} 복사` : role.name,
          description: role.description,
          permissions: copy
            ? role.permissions.filter((code) => allowed.includes(code))
            : [...role.permissions],
        }
      : blankDraft();
    setDraft(next);
    setBaseline({ ...next, permissions: [...next.permissions] });
    setError("");
    setPermissionSearch("");
    setDiscard(false);
    setCopyNotice(
      copy && role && next.permissions.length < role.permissions.length
        ? "내게 없는 권한은 복제에서 제외했습니다."
        : "",
    );
  };
  const closeEditor = () => {
    if (busy) return;
    if (dirty) {
      setDiscard(true);
      return;
    }
    setDraft(null);
    setError("");
  };
  const applyResult = async (result: void | RbacView) => {
    if (onReload) {
      const fresh = await onReload();
      if (!view) setFixture(fresh);
    } else if (result && !view) setFixture(result);
  };
  const save = async () => {
    if (!draft || !canEditDraft || busy) return;
    if (!draft.name.trim()) {
      setError("역할 이름을 입력하세요.");
      return;
    }
    if (
      scopedRoles.some(
        (role) =>
          role.id !== draft.id &&
          role.name.trim().toLocaleLowerCase() ===
            draft.name.trim().toLocaleLowerCase(),
      )
    ) {
      setError("같은 범위에 동일한 이름의 역할이 있습니다.");
      return;
    }
    const addedCodes = draft.permissions.filter(
      (code) => !(original?.permissions ?? []).includes(code),
    );
    if (
      addedCodes.some(
        (code) => !permissions.some((p) => p.code === code && p.grantable),
      )
    ) {
      setError("내게 없는 권한은 다른 역할에 줄 수 없습니다.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...draft,
        name: draft.name.trim(),
        description: draft.description.trim(),
      };
      if (onSave) await applyResult(await onSave(payload));
      else if (!view) {
        let serial = data.roles.length + 1;
        while (data.roles.some((role) => role.id === `custom-${serial}`))
          serial += 1;
        const saved: RbacRole = {
          ...payload,
          id: payload.id ?? `custom-${serial}`,
          builtin: original?.builtin ?? false,
          locked: original?.locked ?? false,
          memberCount: original?.memberCount ?? 0,
        };
        setFixture((previous) => ({
          ...previous,
          roles:
            saved.id === original?.id
              ? previous.roles.map((role) =>
                  role.id === saved.id ? saved : role,
                )
              : [...previous.roles, saved],
        }));
      }
      setDraft(null);
      setNotice("역할을 저장했습니다.");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "역할을 저장하지 못했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };
  const loadMembers = async (role: RbacRole) => {
    setError("");
    setMembersLoaded(false);
    setMembersBusy(true);
    try {
      const result = onLoadMembers
        ? await onLoadMembers(role.id)
        : !view
          ? rbacMemberFixture.slice(0, role.memberCount)
          : role.memberCount === 0
            ? []
            : null;
      if (!result)
        throw new Error("역할이 지정된 사용자 목록을 먼저 불러와야 합니다.");
      setMembers(result);
      setMembersLoaded(true);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "사용자 목록을 불러오지 못했습니다.",
      );
    } finally {
      setMembersBusy(false);
    }
  };
  const openDelete = (role: RbacRole) => {
    if (role.builtin || role.locked || (role.shared && !sharedScope)) return;
    setDeleting(role);
    setMembers([]);
    setDeleteConfirmed(false);
    void loadMembers(role);
  };
  const remove = async () => {
    if (
      !deleting ||
      deleting.builtin ||
      deleting.locked ||
      (deleting.shared && !sharedScope) ||
      busy ||
      !data.canManage ||
      (view && !onDelete) ||
      !membersLoaded ||
      (deleting.memberCount > 0 && !deleteConfirmed)
    )
      return;
    setBusy(true);
    setError("");
    try {
      if (onDelete) await applyResult(await onDelete(deleting.id));
      else if (!view)
        setFixture((previous) => ({
          ...previous,
          roles: previous.roles.filter((role) => role.id !== deleting.id),
        }));
      setDeleting(null);
      setNotice("역할을 삭제했습니다.");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "역할을 삭제하지 못했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };
  const visibleRoles = scopedRoles.filter((role) =>
    `${role.name} ${role.description}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const visiblePermissions = permissions.filter((permission) =>
    `${permission.group} ${permission.label} ${permission.description}`.includes(
      permissionSearch,
    ),
  );
  const groups = [
    ...new Set(visiblePermissions.map((permission) => permission.group)),
  ];
  const updatePermission = (codes: string[], checked: boolean) =>
    setDraft((previous) =>
      previous
        ? {
            ...previous,
            permissions: checked
              ? [...new Set([...previous.permissions, ...codes])]
              : previous.permissions.filter((code) => !codes.includes(code)),
          }
        : previous,
    );
  return (
    <div
      className={`mcr-manager ${!embedded && editable ? "mcr-manager--fab" : ""}`}
    >
      <div className="mcr-context">
        <span className="mcr-context-icon">
          <ShieldCheck size={22} />
        </span>
        <div>
          <strong>
            {data.scope === "staff"
              ? "관리 콘솔 역할"
              : sharedScope
                ? "고객사 공용 역할"
                : `${data.customerName || customerName}의 역할`}
          </strong>
          <p>
            {data.scope === "staff"
              ? "관리자에게 필요한 역할을 만들고, 여러 역할을 함께 지정합니다."
              : sharedScope
                ? "모든 고객사가 함께 사용하는 역할입니다. 변경하면 이 역할을 쓰는 모든 고객사에 적용됩니다."
                : "공용 역할은 읽기만 하고, 이 고객사 전용 역할은 직접 관리합니다."}
          </p>
        </div>
        <Badge tone={data.scope === "staff" ? "blue" : "teal"}>
          {data.scope === "staff"
            ? "관리 콘솔"
            : sharedScope
              ? "모든 고객사"
              : "고객사 역할"}
        </Badge>
      </div>
      {!editable && (
        <p className="mcr-notice">
          <LockKeyhole size={16} />
          <span>
            {data.readOnlyReason ||
              "역할 관리 권한이 없어 읽기만 할 수 있습니다."}
          </span>
        </p>
      )}
      {notice && (
        <p className="mcr-success" role="status">
          <Check size={16} />
          {notice}
        </p>
      )}
      <div className="mcr-toolbar">
        <Field
          type="search"
          label="역할 찾기"
          placeholder="이름 또는 설명으로 찾기"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span className="mcr-muted">
          전체 {scopedRoles.length}개 · 직접 만든 역할{" "}
          {scopedRoles.filter((role) => !role.builtin).length}개
        </span>
        {embedded && editable && (
          <Button onClick={() => begin()}>
            <Plus size={16} />
            {createLabel}
          </Button>
        )}
      </div>
      <div className="mcr-role-list">
        {visibleRoles.map((role) => (
          <article key={role.id} className="mcr-role-row">
            <button
              type="button"
              className="mcr-role-open"
              onClick={() => begin(role)}
              aria-label={`${role.name} 역할 ${editable && !role.locked && (!role.shared || sharedScope) ? "편집" : "보기"}`}
            >
              <span
                className={`mcr-role-icon ${role.locked ? "is-locked" : ""}`}
              >
                {role.locked ? (
                  <LockKeyhole size={18} />
                ) : (
                  <ShieldCheck size={18} />
                )}
              </span>
              <span className="mcr-role-info">
                <strong>
                  {role.name}
                  <Badge tone={role.builtin ? "neutral" : "blue"}>
                    {role.builtin ? "기본" : "직접 만든 역할"}
                  </Badge>
                  {role.locked && <Badge>잠김</Badge>}
                  {data.scope === "customer" && (
                    <Badge tone={role.shared ? "neutral" : "teal"}>
                      {role.shared ? "공용" : "이 고객사 전용"}
                    </Badge>
                  )}
                </strong>
                <small>{role.description}</small>
              </span>
              <span className="mcr-role-counts">
                <span>
                  <Users size={14} />
                  {role.memberCount}명
                </span>
                <span>
                  <ShieldCheck size={14} />
                  권한 {role.permissions.length}개
                </span>
              </span>
              <ChevronRight size={16} className="mcr-muted" />
            </button>
            {editable && (
              <div className="mcr-row-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => begin(role, true)}
                  aria-label={`${role.name} 복제해서 새 역할`}
                >
                  <Copy size={14} />
                  복제
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={
                    role.builtin ||
                    role.locked ||
                    (!!role.shared && !sharedScope) ||
                    (!!view && !onDelete)
                  }
                  title={
                    role.builtin ? "기본 역할은 삭제할 수 없습니다." : undefined
                  }
                  aria-label={`${role.name} 역할 삭제`}
                  onClick={() => openDelete(role)}
                >
                  <Trash2 size={14} />
                  <span>삭제</span>
                </Button>
              </div>
            )}
          </article>
        ))}
      </div>
      {visibleRoles.length === 0 && (
        <div className="mcr-empty">
          <ShieldCheck size={24} />
          <strong>
            {search ? "검색 결과가 없습니다" : "등록된 역할이 없습니다"}
          </strong>
          <p>
            {search
              ? "다른 이름이나 설명으로 찾아보세요."
              : "업무에 맞는 역할을 만들어 주세요."}
          </p>
        </div>
      )}
      {!search && scopedRoles.every((role) => role.builtin) && (
        <div className="mcr-empty">
          <ShieldCheck size={24} />
          <strong>직접 만든 역할이 없습니다</strong>
          <p>기본 역할을 복제하거나 필요한 권한만 골라 새 역할을 만드세요.</p>
        </div>
      )}
      {!embedded && editable && (
        <ConsoleFab label={createLabel} onClick={() => begin()} />
      )}
      <Dialog
        open={!!draft}
        onOpenChange={(open) => {
          if (!open) closeEditor();
        }}
        title={
          draft?.id
            ? `${draft.name || "역할"} · ${locked || sharedReadOnly || !editable ? "권한 보기" : "역할 편집"}`
            : createLabel
        }
        size="detail"
        footer={
          <DialogActions
            secondary={{
              label: canEditDraft ? "취소" : "닫기",
              onClick: closeEditor,
              disabled: busy,
            }}
            primary={
              canEditDraft
                ? {
                    label: draft?.id ? "변경 저장" : createLabel,
                    onClick: save,
                    disabled: busy || (!!draft?.id && !dirty),
                    "aria-busy": busy,
                  }
                : undefined
            }
            note={
              canEditDraft ? (
                <span className="mcr-change-summary">
                  추가 <b>{added}</b> · 제거 <b>{removed}</b>
                  {draft?.sourceRoleId && " · 복제한 역할로 새로 저장"}
                </span>
              ) : undefined
            }
          />
        }
      >
        {draft && (
          <div className="mcr-form">
            <p className="mcr-scope-note">
              {data.scope === "staff"
                ? "관리 콘솔"
                : sharedScope
                  ? "모든 고객사 공용"
                  : original?.shared
                    ? "고객사 공용 · 보기만"
                    : `${data.customerName || customerName} 전용`}{" "}
              <span>
                · {original?.builtin ? "기본 역할" : "직접 만든 역할"}
              </span>
            </p>
            {locked && (
              <p className="mcr-notice">
                <LockKeyhole size={16} />
                <span>
                  잠긴 기본 역할입니다. 권한을 편집하거나 삭제할 수 없습니다.
                  필요한 역할은 복제해서 만드세요.
                </span>
              </p>
            )}
            {!editable && (
              <p className="mcr-notice">
                <LockKeyhole size={16} />
                <span>
                  {data.readOnlyReason ||
                    "역할 관리 권한이 없어 읽기만 할 수 있습니다."}
                </span>
              </p>
            )}
            {sharedReadOnly && (
              <p className="mcr-notice">
                <LockKeyhole size={16} />
                <span>
                  공용 역할은 관리 콘솔에서 관리합니다. 이 고객사에서 쓰려면
                  복제해서 전용 역할을 만드세요.
                </span>
              </p>
            )}
            {copyNotice && <p className="mcr-notice">{copyNotice}</p>}
            <Field
              label="역할 이름"
              required
              maxLength={80}
              value={draft.name}
              disabled={!canEditDraft || busy}
              onChange={(event) =>
                setDraft({ ...draft, name: event.target.value })
              }
            />
            <div className="field">
              <label htmlFor={`${formId}-description`}>설명</label>
              <textarea
                id={`${formId}-description`}
                className="input mcr-description"
                rows={2}
                maxLength={300}
                value={draft.description}
                disabled={!canEditDraft || busy}
                placeholder="이 역할이 담당하는 업무를 설명해 주세요"
                onChange={(event) =>
                  setDraft({ ...draft, description: event.target.value })
                }
              />
            </div>
            <div className="mcr-permission-heading">
              <strong>
                리소스 권한 <span>{draft.permissions.length}개 선택</span>
              </strong>
              <span className="mcr-legend">
                <i />
                읽기 <i />
                쓰기
              </span>
            </div>
            <Field
              label="권한 찾기"
              type="search"
              placeholder="리소스 또는 권한 이름"
              value={permissionSearch}
              onChange={(event) => setPermissionSearch(event.target.value)}
            />
            <div className="mcr-permission-groups">
              {groups.map((group) => {
                const items = visiblePermissions.filter(
                    (p) => p.group === group,
                  ),
                  grantable = items
                    .filter((p) => p.grantable && !p.locked)
                    .map((p) => p.code),
                  checkedCount = items.filter((p) =>
                    draft.permissions.includes(p.code),
                  ).length;
                return (
                  <section className="mcr-permission-group" key={group}>
                    <header>
                      <strong>
                        {group}
                        <span>
                          {checkedCount}/{items.length}
                        </span>
                      </strong>
                      <Checkbox
                        label={`${group} 전체 선택`}
                        checked={
                          grantable.length > 0 &&
                          grantable.every((code) =>
                            draft.permissions.includes(code),
                          )
                        }
                        disabled={
                          !canEditDraft || busy || grantable.length === 0
                        }
                        onChange={(checked) =>
                          updatePermission(grantable, checked)
                        }
                      />
                    </header>
                    <div>
                      {items.map((permission) => (
                        <label
                          key={permission.code}
                          className={`mcr-permission ${!permission.grantable || permission.locked ? "is-restricted" : ""}`}
                        >
                          <input
                            type="checkbox"
                            aria-label={permission.label}
                            checked={draft.permissions.includes(
                              permission.code,
                            )}
                            disabled={
                              !canEditDraft ||
                              busy ||
                              !permission.grantable ||
                              permission.locked
                            }
                            onChange={(event) =>
                              updatePermission(
                                [permission.code],
                                event.target.checked,
                              )
                            }
                          />
                          <span>
                            <strong>
                              {permission.label}
                              <em
                                className={`mcr-access mcr-access--${permission.access}`}
                              >
                                {permission.access === "read" ? "읽기" : "쓰기"}
                              </em>
                            </strong>
                            <small>{permission.description}</small>
                            {(!permission.grantable || permission.locked) && (
                              <small className="mcr-reason">
                                <LockKeyhole size={11} />
                                {permission.locked
                                  ? "필수 권한 · 변경할 수 없음"
                                  : permission.disabledReason ||
                                    "내게 없는 권한"}
                              </small>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </section>
                );
              })}
              {groups.length === 0 && (
                <p className="mcr-empty-inline">일치하는 권한이 없습니다.</p>
              )}
            </div>
            {error && (
              <p role="alert" className="mcr-error">
                {error}
              </p>
            )}
            {discard && (
              <div className="mcr-discard" role="alert">
                <strong>저장하지 않은 변경이 있습니다.</strong>
                <p>이대로 닫으면 작성한 내용이 사라집니다.</p>
                <div>
                  <Button variant="secondary" onClick={() => setDiscard(false)}>
                    계속 편집
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setDraft(null);
                      setDiscard(false);
                    }}
                  >
                    변경 버리고 닫기
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open && !busy) {
            setDeleting(null);
            setError("");
          }
        }}
        title="역할 삭제"
        size="wide"
        footer={
          <DialogActions
            secondary={{
              label: "취소",
              onClick: () => setDeleting(null),
              disabled: busy,
            }}
            primary={{
              label: "역할 삭제",
              tone: "danger",
              onClick: remove,
              disabled:
                busy ||
                membersBusy ||
                !membersLoaded ||
                ((deleting?.memberCount ?? 0) > 0 && !deleteConfirmed) ||
                !data.canManage ||
                (!!view && !onDelete),
              "aria-busy": busy,
            }}
          />
        }
      >
        {deleting && (
          <div className="mcr-form">
            <p>
              <strong>{deleting.name}</strong> 역할을 삭제합니다. 이 역할로
              부여된 권한은 더 이상 사용할 수 없습니다.
            </p>
            {deleting.memberCount > 0 ? (
              <>
                <p className="mcr-notice">
                  <Users size={16} />
                  <span>
                    이 역할을 사용하는 사용자 {deleting.memberCount}명이
                    있습니다. 다른 역할로 가진 권한은 유지됩니다.
                  </span>
                </p>
                <div className="mcr-affected-members" aria-busy={membersBusy}>
                  {membersBusy ? (
                    <p>사용자 목록을 불러오는 중입니다.</p>
                  ) : (
                    members.map((member) => (
                      <div key={member.id}>
                        <span>
                          <strong>{member.name}</strong>
                          {member.jobTitle && <small>{member.jobTitle}</small>}
                        </span>
                        <small>{member.email}</small>
                      </div>
                    ))
                  )}
                </div>
                {membersLoaded && (
                  <Checkbox
                    label="위 사용자에게서 이 역할을 제거하고 삭제하는 데 동의합니다."
                    checked={deleteConfirmed}
                    onChange={setDeleteConfirmed}
                    disabled={busy}
                  />
                )}
              </>
            ) : (
              <p className="mcr-muted">이 역할을 사용하는 사용자가 없습니다.</p>
            )}
            {error && (
              <div className="mcr-error" role="alert">
                {error}
                {!membersLoaded && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void loadMembers(deleting)}
                  >
                    다시 불러오기
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>
      {!view && (
        <RoleAssignmentDialog
          open={assignmentOpen}
          onOpenChange={setAssignmentOpen}
          memberName="김현진"
          selectedIds={["staff-admin", "staff-analyst"]}
          availableRoles={data.roles}
          scope={data.scope}
          customerId={data.customerId}
          lastAdminLockedId="staff-admin"
          onSave={async () => {
            setNotice("역할을 저장했습니다.");
          }}
        />
      )}
    </div>
  );
}
